import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PROGRAM_SUMMARY_KEY } from '../../../components/delivery/widgets/useProgramFinancialSummary';
import { deliveryClient } from '../../../lib/connect/clients';
// ============================================================================
// Financial health — recorded months (upsert-only, one row per calendar
// month per program) + the funding-alert threshold override.
// ============================================================================
const FINANCIAL_MONTH_KEY = 'program financial month';
export function useProgramFinancialMonths(programId) {
    return useQuery({
        queryKey: [
            FINANCIAL_MONTH_KEY,
            'list',
            programId
        ],
        queryFn: async ()=>{
            if (!programId) return {
                items: []
            };
            const resp = await deliveryClient.listProgramFinancialMonths({
                programId
            });
            return {
                items: resp.entries
            };
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
export function useUpsertProgramFinancialMonth() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await deliveryClient.upsertProgramFinancialMonth(input);
            return resp;
        },
        onSuccess: ({ created })=>{
            qc.invalidateQueries({
                queryKey: [
                    FINANCIAL_MONTH_KEY
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_SUMMARY_KEY
                ]
            });
            // The upsert is create-or-update depending on whether the month already
            // had a recorded row — surfacing which one happened is the point of
            // this control (re-submitting the same month is expected, not an error).
            toast.success(created ? 'Month recorded' : 'Month updated');
        },
        onError: (err)=>{
            toast.error(`Failed to record month: ${err.message}`);
        }
    });
}
export function useSetFundingAlertThreshold() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            return deliveryClient.setProgramFundingAlertThreshold(input);
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_SUMMARY_KEY
                ]
            });
            toast.success('Funding alert threshold updated');
        },
        onError: (err)=>{
            toast.error(`Failed to update threshold: ${err.message}`);
        }
    });
}
